import { useEffect, useRef, useState } from "react";
import "./RadarLoader.css";

/**
 * RadarLoader — radar-themed loader family.
 *
 * Three size/context variants:
 *
 *   <RadarLoaderSmall />   ~28px inline spinner. No text, no overlay.
 *                          Use for row-level / inline async operations
 *                          (date pickers, button states, list fetches).
 *
 *   <RadarLoaderLarge />   ~100px radar + \"Cargando...\" label.
 *                          Use as the content of a fullscreen overlay
 *                          (wired into <LoadingOverlay/>) or any
 *                          prominent blocking indicator.
 *
 *   <RadarLoaderMain />    Full modal (~440px) with topbar, log, progress
 *                          and stats. Use for the app-init splash or
 *                          long-running narrated operations.
 *
 * All variants are self-contained: styles live in ./RadarLoader.css
 * and are imported once here (module-deduped by the bundler).
 */

/* ─── Small ─────────────────────────────────────────────────────────────── */

export function RadarLoaderSmall({
  size = 28,
  className = "",
  style,
  ...rest
}) {
  const sizeStyle = size !== 28 ? { "--rl-small-size": `${size}px` } : null;
  return (
    <div
      className={`radar-loader-root radar-loader-small ${className}`}
      role="status"
      aria-label="Cargando"
      style={sizeStyle ? { ...sizeStyle, ...style } : style}
      {...rest}
    >
      <div className="radar-face" aria-hidden="true">
        <div className="ring r1" />
        <div className="axis h" />
        <div className="axis v" />
        <div className="sweep" />
        <div className="origin" />
      </div>
    </div>
  );
}

/* ─── Large ─────────────────────────────────────────────────────────────── */

export function RadarLoaderLarge({
  label = "Cargando",
  subLabel = null,
  className = "",
}) {
  return (
    <div
      className={`radar-loader-root radar-loader-large ${className}`}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div className="radar-face" aria-hidden="true">
        <div className="ring r1" />
        <div className="ring r2" />
        <div className="axis h" />
        <div className="axis v" />
        <div className="sweep" />
        <div className="origin" />
      </div>
      <div className="rl-label">
        {label}
        <span className="rl-dots" aria-hidden="true">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </div>
      {subLabel && <div className="rl-sublabel">{subLabel}</div>}
    </div>
  );
}

/* ─── Main (narrated modal) ─────────────────────────────────────────────── */

/**
 * Step shape:
 *   tag   — short uppercase label shown between brackets in the log line
 *   type  — "ok" | "warn" (controls tag color)
 *   msg   — log line message
 *   sub   — subtitle shown above the log while this step is active
 *   pct   — progress percentage (0–100)
 *   stats — optional [{label, value}, {label, value}, {label, value}]
 *           overrides the default stats grid for this step. If omitted,
 *           falls back to the legacy sat/dev/lat fields for GPS narratives.
 */
export const AUTH_STEPS = [
  {
    tag: "AUTH",
    type: "ok",
    msg: "Validando credenciales",
    sub: "Consultando identidad",
    pct: 12,
    stats: [
      { label: "Intentos", value: "01/03" },
      { label: "Sesión", value: "—" },
      { label: "Latencia", value: "142 ms" },
    ],
  },
  {
    tag: "MFA",
    type: "ok",
    msg: "Solicitando código OTP",
    sub: "Enviando token al correo registrado",
    pct: 28,
    stats: [
      { label: "Intentos", value: "01/03" },
      { label: "Sesión", value: "—" },
      { label: "Latencia", value: "118 ms" },
    ],
  },
  {
    tag: "OTP",
    type: "ok",
    msg: "Verificando código",
    sub: "Validando token de un solo uso",
    pct: 48,
    stats: [
      { label: "Intentos", value: "01/03" },
      { label: "OTP", value: "6 dig" },
      { label: "Latencia", value: "94 ms" },
    ],
  },
  {
    tag: "JWT",
    type: "ok",
    msg: "Emitiendo token de sesión",
    sub: "Firmando JWT",
    pct: 68,
    stats: [
      { label: "Token", value: "JWT" },
      { label: "TTL", value: "24 h" },
      { label: "Latencia", value: "72 ms" },
    ],
  },
  {
    tag: "PERM",
    type: "ok",
    msg: "Cargando permisos",
    sub: "Resolviendo rol y scopes",
    pct: 82,
    stats: [
      { label: "Rol", value: "USER" },
      { label: "Scopes", value: "04" },
      { label: "Latencia", value: "58 ms" },
    ],
  },
  {
    tag: "SESS",
    type: "ok",
    msg: "Abriendo sesión",
    sub: "Preparando workspace",
    pct: 95,
    stats: [
      { label: "Sesión", value: "LIVE" },
      { label: "Scopes", value: "04" },
      { label: "Latencia", value: "44 ms" },
    ],
  },
  {
    tag: "OK",
    type: "ok",
    msg: "Acceso autorizado",
    sub: "Redirigiendo al panel",
    pct: 100,
    stats: [
      { label: "Sesión", value: "LIVE" },
      { label: "Rol", value: "USER" },
      { label: "Latencia", value: "38 ms" },
    ],
  },
];

// Default narrative — matches the app's tech stack and the user flow at
// init. Consumers can override via the `steps` prop.
const DEFAULT_STEPS = [
  {
    tag: "INIT",
    type: "ok",
    msg: "Iniciando módulo TCP",
    sub: "Abriendo socket en puerto 6023",
    sat: 2,
    dev: 0,
    lat: null,
    pct: 8,
  },
  {
    tag: "NET",
    type: "ok",
    msg: "Conectando APN Claro Colombia",
    sub: "Autenticando APN",
    sat: 4,
    dev: 0,
    lat: 284,
    pct: 20,
  },
  {
    tag: "GPS",
    type: "ok",
    msg: "Sincronizando con satélites",
    sub: "Adquiriendo señal de satélites",
    sat: 7,
    dev: 1,
    lat: 192,
    pct: 38,
  },
  {
    tag: "DEV",
    type: "ok",
    msg: "Handshake GT06",
    sub: "Validando IMEI",
    sat: 9,
    dev: 1,
    lat: 156,
    pct: 55,
  },
  {
    tag: "WS",
    type: "ok",
    msg: "Canal WebSocket activo",
    sub: "Stream en tiempo real",
    sat: 10,
    dev: 2,
    lat: 142,
    pct: 70,
  },
  {
    tag: "DB",
    type: "ok",
    msg: "Cargando dispositivos",
    sub: "Consultando PostgreSQL",
    sat: 11,
    dev: 3,
    lat: 118,
    pct: 88,
  },
  {
    tag: "OK",
    type: "ok",
    msg: "Sistema listo",
    sub: "Monitoreo activo",
    sat: 12,
    dev: 3,
    lat: 88,
    pct: 100,
  },
];

function nowTimestamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function RadarLoaderMain({
  title = "Estableciendo conexión",
  steps = DEFAULT_STEPS,
  topLabel = "SYS.INIT",
  deviceLabel = "GT06 · v2.4.1",
  locationLabel = "VALLEDUPAR · CO",
  doneLabel = "Sistema operativo",
  loop = false,
  loopPauseMs = 1200,
  speed = 1,
  scale = 1,
  className = "",
  style,
}) {
  const [index, setIndex] = useState(0);
  const [logLines, setLogLines] = useState([]);
  const timerRef = useRef(null);

  // Stable reference so the effect doesn't re-fire on every render when the
  // caller inlines the steps prop (which would otherwise reset progress).
  const stepsRef = useRef(steps);
  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  useEffect(() => {
    const list = stepsRef.current;

    // Past the last step: if `loop` is enabled, wait `loopPauseMs` showing the
    // final state, then reset the log and start over. Otherwise stop here.
    if (index >= list.length) {
      if (!loop) return;
      timerRef.current = setTimeout(() => {
        setLogLines([]);
        setIndex(0);
      }, loopPauseMs / speed);
      return () => clearTimeout(timerRef.current);
    }

    const current = list[index];
    setLogLines((prev) => {
      const line = {
        id: `${Date.now()}-${index}`,
        ts: nowTimestamp(),
        tag: current.tag,
        type: current.type,
        msg: current.msg,
      };
      // Cap the log buffer to the last ~6 so the container doesn't scroll.
      const next = [...prev, line];
      return next.length > 6 ? next.slice(next.length - 6) : next;
    });

    const delay = (650 + Math.random() * 400) / speed;
    timerRef.current = setTimeout(() => {
      setIndex((i) => Math.min(i + 1, list.length));
    }, delay);

    return () => clearTimeout(timerRef.current);
  }, [index, loop, loopPauseMs, speed]);

  const current =
    steps[Math.min(index, steps.length - 1)] ?? steps[steps.length - 1];
  const isDone = index >= steps.length;

  const scaleStyle = scale !== 1 ? { "--rl-main-scale": scale } : null;
  return (
    <div
      className={`radar-loader-root radar-loader-main ${className}`}
      role="dialog"
      aria-live="polite"
      aria-label={title}
      style={scaleStyle ? { ...scaleStyle, ...style } : style}
    >
      {/* Top bar */}
      <div className="rl-topbar">
        <div className="left">
          <span className="rl-led" aria-hidden="true" />
          <span>SYS.INIT</span>
        </div>
        <div className="right">{deviceLabel}</div>
      </div>

      {/* Radar */}
      <div className="rl-radar-wrap">
        <div className="radar-face" aria-hidden="true">
          <div className="corner tl" />
          <div className="corner tr" />
          <div className="corner bl" />
          <div className="corner br" />
          <div className="ring r1" />
          <div className="ring r2" />
          <div className="ring r3" />
          <div className="ring r4" />
          <div className="axis h" />
          <div className="axis v" />
          <div className="degree n">N 00°</div>
          <div className="degree e">E 90°</div>
          <div className="degree s">S 180°</div>
          <div className="degree w">W 270°</div>
          <div className="sweep" />
          <div className="blip b1" />
          <div className="blip b2" />
          <div className="blip b3" />
          <div className="origin origin-pulse" />
        </div>
      </div>

      {/* Content */}
      <div className="rl-content">
        <div className="rl-title">{title}</div>
        <div className="rl-subtitle">
          {isDone ? doneLabel : current.sub}
          {!isDone && <span className="rl-cursor" aria-hidden="true" />}
        </div>

        {/* Log */}
        <div className="rl-log" aria-hidden="true">
          {logLines.map((line) => (
            <div key={line.id} className="rl-log-line">
              <span className="ts">{line.ts}</span>
              <span className={`tag ${line.type === "warn" ? "warn" : "ok"}`}>
                [{line.tag}]
              </span>
              <span className="msg">{line.msg}</span>
            </div>
          ))}
        </div>

        {/* Progress */}
        <div className="rl-progress-row">
          <span>Progreso</span>
          <span className="pct">{current.pct}%</span>
        </div>
        <div className="rl-progress">
          <div
            className="rl-progress-fill"
            style={{ width: `${current.pct}%` }}
          />
        </div>

        {/* Stats — use custom stats if step provides them, otherwise fall back
            to the legacy sat/dev/lat fields (GPS narrative). */}
        <div className="rl-stats">
          {current.stats
            ? current.stats.map((s, i) => (
                <div key={i} className="rl-stat">
                  <div className="label">{s.label}</div>
                  <div className="value">{s.value}</div>
                </div>
              ))
            : (
              <>
                <div className="rl-stat">
                  <div className="label">Satélites</div>
                  <div className="value">
                    {String(current.sat ?? 0).padStart(2, "0")}/12
                  </div>
                </div>
                <div className="rl-stat">
                  <div className="label">Dispositivos</div>
                  <div className="value">{current.dev ?? 0}</div>
                </div>
                <div className="rl-stat">
                  <div className="label">Latencia</div>
                  <div className="value">
                    {current.lat ? `${current.lat} ms` : "-- ms"}
                  </div>
                </div>
              </>
            )}
        </div>
      </div>

      {/* Footer */}
      <div className="rl-footer">
        <div>{locationLabel}</div>
        <div>
          SEÑAL
          <span className="rl-signal" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </span>
        </div>
      </div>
    </div>
  );
}

export default RadarLoaderLarge;
